import { useScreenView } from '../../../../../utils/ScreenViewContext';
import SavingsWidget from '../home/widgets/SavingsWidget';
import BudgetCategories from './widgets/monthly-budget/BudgetCategories';
import { useQuery } from '@tanstack/react-query';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import {
  CentsPerDollarBreakdownData,
  MonthlyBudgetMetrics,
  TruckBudgetedVsSpentData,
  CustomerWithDriverOnboarding,
} from '../../../../../types/apiTypes';
import DollarBreakdown from './widgets/monthly-budget/DollarBreakdown';
import TrucksBudgetedSpentStackedBars from './widgets/monthly-budget/TrucksBudgetedSpentStackedBars';
import { useMemo } from 'react';
import {
  isPreActiveCustomer,
  getDemoBudgetItems,
  getDemoMonthlyBudgetMetrics,
  getDemoDollarBreakdownData,
  getDemoTrucksBudgetedSpentData,
} from '../home/utils/demoData';
import { DemoDataAlert } from '../home/components/DemoDataAlert';

interface MonthlyBudgetProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function MonthlyBudget({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: MonthlyBudgetProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userId, userRecord } = useUser();

  // ============================================================================
  // Pre-Active Customer Detection
  // ============================================================================

  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;

  const { data: budgetItems = [], isLoading: loadingSavingsCategories } =
    useQuery({
      queryKey: ['budgetItems', userId ?? ''],
      queryFn: async () => {
        const response = await callProtectedApi(
          `/budgetItems/${userId ?? ''}`,
          'get',
          {}
        );
        return response?.data?.data?.budgetItems || [];
      },
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000, // 15 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
    });

  const fetchMonthlyBudgetMetricsData = async () => {
    const response = await callProtectedApi(
      `/budgetItems/metrics/${userId ?? ''}`,
      'get',
      {}
    );
    return response?.data?.data?.monthlyBudgetMetrics;
  };

  const {
    data: monthlyBudgetMetricsData,
    isLoading: loadingMonthlyBudgetMetricsData,
  } = useQuery<MonthlyBudgetMetrics[]>({
    queryKey: ['monthlyBudgetMetrics', userId],
    queryFn: () => fetchMonthlyBudgetMetricsData(),
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const fetchDollarBreakdownData = async () => {
    const response = await callProtectedApi(
      `/charts/cents-per-dollar-breakdown-chart/${userId ?? ''}`,
      'get',
      {}
    );
    return response?.data?.data?.dollarBreakdown;
  };

  const { data: dollarBreakdownData, isLoading: loadingDollarBreakdownData } =
    useQuery<CentsPerDollarBreakdownData[]>({
      queryKey: ['dollarBreakdownData', userId],
      queryFn: () => fetchDollarBreakdownData(),
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const fetchTrucksBudgetedSpentData = async () => {
    const start_date = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1
    )
      .toISOString()
      .split('T')[0]; // First day of the current month
    const end_date = new Date().toISOString().split('T')[0]; // Current day
    const response = await callProtectedApi(
      `/charts/trucks-budgeted-spent/${userId ?? ''}/all?start_date=${start_date}&end_date=${end_date}`,
      'get',
      {}
    );
    return response?.data?.data?.trucksBudgetedSpentData;
  };

  const {
    data: trucksBudgetedSpentData,
    isLoading: loadingTrucksBudgetedSpentData,
  } = useQuery<TruckBudgetedVsSpentData[]>({
    queryKey: ['trucksBudgetedSpentData', userId],
    queryFn: () => fetchTrucksBudgetedSpentData(),
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // ============================================================================
  // Data Resolution: Use demo data for pre-active customers, real data otherwise
  // ============================================================================

  const displayBudgetItems = useMemo(() => {
    return isPreActive ? getDemoBudgetItems() : budgetItems;
  }, [isPreActive, budgetItems]);

  const displayMonthlyBudgetMetricsData = useMemo(() => {
    return isPreActive
      ? getDemoMonthlyBudgetMetrics()
      : (monthlyBudgetMetricsData ?? []);
  }, [isPreActive, monthlyBudgetMetricsData]);

  const displayDollarBreakdownData = useMemo(() => {
    return isPreActive
      ? getDemoDollarBreakdownData()
      : (dollarBreakdownData ?? []);
  }, [isPreActive, dollarBreakdownData]);

  const displayTrucksBudgetedSpentData = useMemo(() => {
    return isPreActive
      ? getDemoTrucksBudgetedSpentData()
      : (trucksBudgetedSpentData ?? []);
  }, [isPreActive, trucksBudgetedSpentData]);

  // Loading states: Never show loading for demo data
  const displayLoadingSavingsCategories = isPreActive
    ? false
    : loadingSavingsCategories;
  const displayLoadingMonthlyBudgetMetricsData = isPreActive
    ? false
    : loadingMonthlyBudgetMetricsData;
  const displayLoadingDollarBreakdownData = isPreActive
    ? false
    : loadingDollarBreakdownData;
  const displayLoadingTrucksBudgetedSpentData = isPreActive
    ? false
    : loadingTrucksBudgetedSpentData;

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="flex h-full w-[1148px] max-w-[1148px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden py-[16px]">
            {isPreActive && <DemoDataAlert />}
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <SavingsWidget
                seeAll={true}
                budgetItems={displayBudgetItems}
                isLoading={displayLoadingSavingsCategories}
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex max-h-[600px] w-full flex-row items-start justify-start gap-x-[16px] align-middle">
              <TrucksBudgetedSpentStackedBars
                addClass="w-full h-[600px] min-h-[600px]"
                trucksData={displayTrucksBudgetedSpentData}
                loading={displayLoadingTrucksBudgetedSpentData}
                isPreActive={isPreActive}
              />
              <BudgetCategories
                addClass="w-full h-[600px] min-h-[600px] max-h-[600px]"
                monthlyBudgetMetricsData={displayMonthlyBudgetMetricsData}
                loadingMonthlyBudgetMetricsData={
                  displayLoadingMonthlyBudgetMetricsData
                }
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <DollarBreakdown
                dollarBreakdownData={displayDollarBreakdownData}
                loadingDollarBreakdownData={displayLoadingDollarBreakdownData}
                isPreActive={isPreActive}
              />
            </div>
          </div>
        );
      case 'tablet':
        return (
          <div className="flex h-full w-[568px] max-w-[568px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden py-[16px]">
            {isPreActive && <DemoDataAlert />}
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <SavingsWidget
                seeAll={true}
                budgetItems={displayBudgetItems}
                isLoading={displayLoadingSavingsCategories}
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <TrucksBudgetedSpentStackedBars
                addClass="w-full"
                trucksData={displayTrucksBudgetedSpentData}
                loading={displayLoadingTrucksBudgetedSpentData}
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <BudgetCategories
                monthlyBudgetMetricsData={displayMonthlyBudgetMetricsData}
                loadingMonthlyBudgetMetricsData={
                  displayLoadingMonthlyBudgetMetricsData
                }
                addClass="w-full"
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <DollarBreakdown
                dollarBreakdownData={displayDollarBreakdownData}
                loadingDollarBreakdownData={displayLoadingDollarBreakdownData}
                isPreActive={isPreActive}
              />
            </div>
          </div>
        );

      case 'mobile':
        return (
          <div className="flex h-full w-[351px] max-w-[351px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden py-[16px]">
            {isPreActive && <DemoDataAlert />}
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <SavingsWidget
                seeAll={true}
                budgetItems={displayBudgetItems}
                isLoading={displayLoadingSavingsCategories}
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <TrucksBudgetedSpentStackedBars
                addClass="w-full h-[640px] min-h-[640px]"
                trucksData={displayTrucksBudgetedSpentData}
                loading={displayLoadingTrucksBudgetedSpentData}
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <BudgetCategories
                monthlyBudgetMetricsData={displayMonthlyBudgetMetricsData}
                loadingMonthlyBudgetMetricsData={
                  displayLoadingMonthlyBudgetMetricsData
                }
                addClass="w-full"
                isPreActive={isPreActive}
              />
            </div>
            <div className="flex w-full flex-row items-center justify-center align-middle">
              <DollarBreakdown
                dollarBreakdownData={displayDollarBreakdownData}
                loadingDollarBreakdownData={displayLoadingDollarBreakdownData}
                isPreActive={isPreActive}
              />
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default MonthlyBudget;
